import { IonPage } from '@ionic/react'
import { AnimatePresence } from 'framer-motion'
import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import {
  CompleteRegistrationForm,
  ForgotPasswordByUsernameForm,
  ForgotPasswordForm,
  LoginForm,
  PasswordResetForm
} from '@apps-common/apps/my-monitoring/pages/authenticate/components'
import { MyMonitoringLogoColor } from '@apps-common/components/common/logos'
import { LayoutContainer } from '@apps-common/components/layout'
import {
  AUTHENTICATE_OPTION_COMPLETE_REGISTRATION,
  AUTHENTICATE_OPTION_FORGOT_PASS,
  AUTHENTICATE_OPTION_FORGOT_PASS_USERNAME,
  AUTHENTICATE_OPTION_LOGIN,
  AUTHENTICATE_OPTION_RESET_PASS,
  AUTHENTICATE_SCREEN_OPTIONS
} from '@apps-common/config/authenticate-options'
import { useWindowDimensions } from '@apps-common/hooks/useWindowDimensions'
import { useAuthenticateScreenStore } from '@apps-common/stores'

export const AuthenticationPage = () => {
  const { isMobile } = useWindowDimensions()
  const { screen, setScreen, resetScreen } = useAuthenticateScreenStore()
  const location = useLocation()

  const authParams = useMemo(() => {
    const params = new URLSearchParams(location.search)

    return {
      requestedScreen: params.get('screen'),
      code: params.get('code')
    }
  }, [location.search])

  useEffect(() => {
    const requestedScreen = authParams.requestedScreen?.toLowerCase()
    const code = authParams.code

    const matchedScreen = AUTHENTICATE_SCREEN_OPTIONS.find((opt) => opt.toLowerCase() === requestedScreen)

    if (matchedScreen) {
      setScreen(matchedScreen)
      return
    }

    if (code) {
      setScreen(AUTHENTICATE_OPTION_RESET_PASS)
      return
    }

    resetScreen()
  }, [authParams.requestedScreen, authParams.code, setScreen, resetScreen])

  return (
    <IonPage className='auth-no-scroll-page'>
      <LayoutContainer
        noSidebar
        ionClasses='auth-no-scroll'
        bgClasses='!relative !bg-transparent z-[10]'
        ionStyle={{
          '--background': 'transparent'
        }}
        scrollY={false}
        contentScrollable={false}
      >
        <div className='relative min-h-[max(100dvh,600px)] overflow-hidden flex flex-col items-center justify-start sm:justify-center w-full sm:px-4 bg-bg'>
          <div className='absolute top-0 left-0 w-full z-[1] flex items-start justify-center pointer-events-none pt-[max(21dvh,40px)] sm:pt-[max(14dvh,50px)]'>
            <MyMonitoringLogoColor containerClasses='max-w-[260px] mx-auto' />
          </div>

          <div className='relative z-[2] w-full flex items-end sm:items-center justify-center h-[max(100dvh,600px)]'>
            <div className='w-full sm:max-w-[520px] sm:min-w-[450px] flex justify-center'>
              <AnimatePresence mode='wait'>
                {screen === AUTHENTICATE_OPTION_LOGIN && <LoginForm key='login' isMobile={isMobile} />}

                {screen === AUTHENTICATE_OPTION_FORGOT_PASS && <ForgotPasswordForm key='forgot-password' isMobile={isMobile} />}

                {screen === AUTHENTICATE_OPTION_FORGOT_PASS_USERNAME && (
                  <ForgotPasswordByUsernameForm key='forgot-password-username' isMobile={isMobile} />
                )}

                {screen === AUTHENTICATE_OPTION_RESET_PASS && (
                  <PasswordResetForm key='reset-password' isMobile={isMobile} code={authParams.code || undefined} />
                )}

                {screen === AUTHENTICATE_OPTION_COMPLETE_REGISTRATION && (
                  <CompleteRegistrationForm key='complete-registration' isMobile={isMobile} code={authParams.code || undefined} />
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </LayoutContainer>
    </IonPage>
  )
}
